import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { isCircuitOpen } from "@/lib/circuit-breaker";
import { showBrowserNotification } from "@/lib/browser-notifications";
import { buildInboundMessageNotificationForLead } from "@/lib/notification-copy";
import { buildConversationInboxUrl } from "@/lib/conversation-inbox-url";
import { looksLikePhoneName } from "@/lib/lead-display-name";
import { CONVERSATION_INBOX_PATCH_EVENT } from "@/lib/conversation-inbox-patch";
import { loadConversationReadTimestamps } from "@/lib/conversation-list-map";
import { shouldReceiveConversationPush } from "@/lib/push-notification-eligibility";
import { fetchInboxUnreadPreviews, fetchInboxUnreadTotal } from "@/lib/inbox-stats";
import { patchKnownUnreadPreview } from "@/lib/unread-preview-patch";
import { useWorkspaceConversationRealtime } from "@/hooks/useWorkspaceConversationRealtime";
import {
  REALTIME_PAUSE_ON_HIDDEN_ENABLED,
  type ConversationChangeHandler,
} from "@/lib/workspace-conversation-realtime-hub";

const PREVIEW_LIMIT = 15;
/** Fallback poll — realtime + debounce cobrem o fluxo normal; poll só reconcilia. */
const UNREAD_RECONCILE_POLL_MS = 5 * 60 * 1000;

export type SidebarChannel = "whatsapp" | "instagram" | "messenger";

export interface SidebarUnreadMaps {
  byChannel: Record<SidebarChannel, number>;
  byIntegration: Record<string, number>;
}

export const EMPTY_SIDEBAR_UNREADS: SidebarUnreadMaps = {
  byChannel: { whatsapp: 0, instagram: 0, messenger: 0 },
  byIntegration: {},
};

function buildSidebarUnreadMaps(rows: UnreadConversationPreview[]): SidebarUnreadMaps {
  const byIntegration: Record<string, number> = {};
  const byChannel: Record<SidebarChannel, number> = {
    whatsapp: 0,
    instagram: 0,
    messenger: 0,
  };

  for (const row of rows) {
    if (row.integrationId) {
      byIntegration[row.integrationId] = (byIntegration[row.integrationId] || 0) + 1;
    }
    byChannel[row.channel]++;
  }

  return { byChannel, byIntegration };
}

export type UnreadConversationPreview = {
  conversationId: string;
  channel: "whatsapp" | "instagram" | "messenger";
  integrationId?: string | null;
  leadName: string;
  leadAvatarUrl?: string;
  preview: string;
  lastMessageAt: string;
  isOutbound: boolean;
  unreadCount: number;
};

type WindowWithWebkitAudio = Window & typeof globalThis & {
  webkitAudioContext?: typeof AudioContext;
};

function playNotificationSound() {
  try {
    const AudioContextCtor = window.AudioContext || (window as WindowWithWebkitAudio).webkitAudioContext;
    if (!AudioContextCtor) return;

    const ctx = new AudioContextCtor();
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);

    oscillator.type = "sine";
    oscillator.frequency.setValueAtTime(800, ctx.currentTime);

    gainNode.gain.setValueAtTime(0.3, ctx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.15);

    oscillator.start(ctx.currentTime);
    oscillator.stop(ctx.currentTime + 0.15);

    oscillator.onended = () => ctx.close();
  } catch {
    // Browser may block audio before user interaction
  }
}

function parseChannel(value: string | null | undefined): UnreadConversationPreview["channel"] {
  if (value === "whatsapp" || value === "instagram" || value === "messenger") return value;
  return "whatsapp";
}

function leadAvatarFromMetadata(metadata: unknown): string | undefined {
  if (!metadata || typeof metadata !== "object") return undefined;
  const m = metadata as Record<string, unknown>;
  const pic = m.instagram_profile_pic ?? m.profile_picture;
  return typeof pic === "string" ? pic : undefined;
}

function isUnreadForUser(
  conversationId: string,
  unreadCount: number,
  lastMessageAt: string | null,
  readTimestamps: Map<string, string>
): boolean {
  if (unreadCount <= 0 || !lastMessageAt) return false;
  const readAt = readTimestamps.get(conversationId);
  if (readAt && lastMessageAt <= readAt) return false;
  return true;
}

export function useUnreadNotifications(workspaceId: string | null, disabled = false) {
  /** Lista completa de threads unread (fonte única para header + sidebar). */
  const [unreadConversations, setUnreadConversations] = useState<UnreadConversationPreview[]>([]);
  const unreadConversationsRef = useRef<UnreadConversationPreview[]>([]);
  useEffect(() => {
    unreadConversationsRef.current = unreadConversations;
  }, [unreadConversations]);
  /** Badge COUNT(*) from RPC; null until a successful fetch (or previews fallback). */
  const [unreadBadgeTotal, setUnreadBadgeTotal] = useState<number | null>(null);
  const prevCountRef = useRef(0);
  const notifiedMessagesRef = useRef<Set<string>>(new Set());
  const notifiedEscalationsRef = useRef<Set<string>>(new Set());
  const leadNameByConversationRef = useRef<Map<string, string>>(new Map());
  const storageKey = `conversation-read-timestamps-${workspaceId}`;
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const readTimestamps = useRef<Map<string, string>>(new Map());
  const checkInFlightRef = useRef<Promise<void> | null>(null);
  const checkPendingRef = useRef(false);
  const currentUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (disabled) {
      currentUserIdRef.current = null;
      return;
    }
    void supabase.auth.getUser().then(({ data }) => {
      currentUserIdRef.current = data.user?.id ?? null;
    });
  }, [disabled, workspaceId]);

  const syncReadTimestampsFromStorage = useCallback(() => {
    if (disabled || !workspaceId) {
      readTimestamps.current = new Map();
      return;
    }
    readTimestamps.current = loadConversationReadTimestamps(workspaceId);
  }, [workspaceId, disabled]);

  useEffect(() => {
    syncReadTimestampsFromStorage();
  }, [syncReadTimestampsFromStorage]);

  const checkUnreadMessages = useCallback(async () => {
    if (disabled) return;
    if (!workspaceId) {
      setUnreadConversations([]);
      setUnreadBadgeTotal(null);
      return;
    }

    if (checkInFlightRef.current) {
      checkPendingRef.current = true;
      return checkInFlightRef.current;
    }

    const run = async () => {
      try {
        do {
          checkPendingRef.current = false;
          if (isCircuitOpen()) return;

          syncReadTimestampsFromStorage();

          try {
            const [previewsResult, totalResult] = await Promise.allSettled([
              fetchInboxUnreadPreviews(supabase, workspaceId),
              fetchInboxUnreadTotal(supabase, workspaceId),
            ]);

            const rows =
              previewsResult.status === "fulfilled" ? previewsResult.value : null;
            if (previewsResult.status === "rejected") {
              console.error("Erro ao verificar não lidas:", previewsResult.reason);
            }

            const totalFromRpc =
              totalResult.status === "fulfilled" ? totalResult.value : null;
            if (totalResult.status === "rejected") {
              console.error("Erro ao contar não lidas:", totalResult.reason);
            }

            if (rows != null) {
              const unreadRows: UnreadConversationPreview[] = [];
              let overlayHidden = 0;

              for (const conv of rows) {
                const lastMessageAt = conv.last_message_at;
                const unread = typeof conv.unread_count === "number" ? conv.unread_count : 0;
                if (!isUnreadForUser(conv.conversation_id, unread, lastMessageAt, readTimestamps.current)) {
                  // Server total ignores localStorage; subtract overlay-hidden rows
                  // so the badge does not jump back up after "I already read this".
                  if (unread > 0) overlayHidden += 1;
                  continue;
                }

                const leadName = buildInboundMessageNotificationForLead(
                  {
                    name: conv.lead_name,
                    phone: conv.lead_phone,
                    metadata: conv.lead_metadata,
                    channel: conv.channel,
                    externalUserId: conv.external_user_id,
                  },
                  null,
                ).title.slice(0, 80);
                leadNameByConversationRef.current.set(conv.conversation_id, leadName);
                unreadRows.push({
                  conversationId: conv.conversation_id,
                  channel: parseChannel(conv.channel),
                  integrationId: conv.integration_id,
                  leadName,
                  leadAvatarUrl: leadAvatarFromMetadata(conv.lead_metadata),
                  preview:
                    (conv.last_message_preview)?.trim() ||
                    "Nova mensagem",
                  lastMessageAt: lastMessageAt || new Date().toISOString(),
                  isOutbound: conv.last_message_direction === "outbound",
                  unreadCount: unread,
                });
              }

              setUnreadConversations(unreadRows);
              // Total RPC 404/error → badge from visible previews; never zero from error.
              if (totalFromRpc != null) {
                setUnreadBadgeTotal(Math.max(0, totalFromRpc - overlayHidden));
              } else {
                setUnreadBadgeTotal(unreadRows.length);
              }
            } else if (totalFromRpc != null) {
              setUnreadBadgeTotal(totalFromRpc);
            }
          } catch (err) {
            console.error("Erro ao verificar não lidas:", err);
          }
        } while (checkPendingRef.current);
      } finally {
        checkInFlightRef.current = null;
        // Race: pending set após o while e antes de liberar in-flight.
        if (checkPendingRef.current) {
          checkPendingRef.current = false;
          void checkUnreadMessages();
        }
      }
    };

    const promise = run();
    checkInFlightRef.current = promise;
    return promise;
  }, [workspaceId, disabled, syncReadTimestampsFromStorage]);

  const debouncedCheck = useCallback(() => {
    if (disabled) return;
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      checkUnreadMessages();
    }, 2000);
  }, [checkUnreadMessages, disabled]);

  const notifyFromConversationChange = useCallback((record: Record<string, unknown>) => {
    if (disabled || !workspaceId) return;

    syncReadTimestampsFromStorage();

    const conversationId = typeof record.id === "string" ? record.id : null;
    if (!conversationId) return;

    const lastMessageAt = typeof record.last_message_at === "string" ? record.last_message_at : null;
    const unread = typeof record.unread_count === "number" ? record.unread_count : 0;
    const direction = typeof record.last_message_direction === "string" ? record.last_message_direction : null;
    const readAt = readTimestamps.current.get(conversationId);

    const leadId = typeof record.lead_id === "string" ? record.lead_id : null;
    const channel = typeof record.channel === "string" ? record.channel : null;
    const integrationId =
      typeof record.integration_id === "string" ? record.integration_id : null;

    const verifyAndNotify = async (notify: () => void) => {
      let userId = currentUserIdRef.current;
      if (!userId) {
        const { data } = await supabase.auth.getUser();
        userId = data.user?.id ?? null;
        currentUserIdRef.current = userId;
      }
      if (!userId) return;

      const eligible = await shouldReceiveConversationPush(
        workspaceId,
        userId,
        leadId,
        channel,
        integrationId,
      );
      if (!eligible) return;
      notify();
    };

    if (
      unread > 0 &&
      direction !== "outbound" &&
      lastMessageAt &&
      (!readAt || lastMessageAt > readAt)
    ) {
      const notificationKey = `${conversationId}:${lastMessageAt}`;
      if (!notifiedMessagesRef.current.has(notificationKey)) {
        notifiedMessagesRef.current.add(notificationKey);
        const preview =
          typeof record.last_message_preview === "string" ? record.last_message_preview : null;
        const externalUserId =
          typeof record.external_user_id === "string" ? record.external_user_id : null;

        void (async () => {
          await verifyAndNotify(async () => {
            let cachedName = leadNameByConversationRef.current.get(conversationId);
            let leadInput = {
              name: cachedName,
              phone: null as string | null,
              metadata: null as Record<string, unknown> | null,
              channel,
              externalUserId,
              pushName: null as string | null,
            };

            if (
              leadId &&
              (!cachedName || cachedName === "Contato" || looksLikePhoneName(cachedName))
            ) {
              const { data } = await supabase
                .from("leads")
                .select("name, phone, metadata")
                .eq("id", leadId)
                .maybeSingle();
              if (data) {
                leadInput = {
                  ...leadInput,
                  name: data.name,
                  phone: data.phone,
                  metadata: (data.metadata as Record<string, unknown> | null) ?? null,
                };
              }

              const { data: recentMsg } = await supabase
                .from("messages")
                .select("metadata")
                .eq("conversation_id", conversationId)
                .order("created_at", { ascending: false })
                .limit(1)
                .maybeSingle();
              const push = (recentMsg?.metadata as Record<string, unknown> | null)?.pushName;
              if (typeof push === "string" && push.trim()) {
                leadInput.pushName = push.trim();
              }
            }

            const { title, body } = buildInboundMessageNotificationForLead(leadInput, preview);
            leadNameByConversationRef.current.set(conversationId, title);
            showBrowserNotification({
              title,
              body,
              tag: `fabchat-message-${conversationId}`,
              url: buildConversationInboxUrl({
                conversationId,
                channel: parseChannel(channel),
                integrationId,
              }),
            });
          });
        })();
      }
    }

    const escalatedAt = typeof record.escalated_by_agent_at === "string" ? record.escalated_by_agent_at : null;
    if (escalatedAt) {
      const escalationKey = `${conversationId}:${escalatedAt}`;
      if (!notifiedEscalationsRef.current.has(escalationKey)) {
        notifiedEscalationsRef.current.add(escalationKey);
        const escalationChannel =
          typeof record.channel === "string" ? record.channel : null;
        const escalationIntegrationId =
          typeof record.integration_id === "string" ? record.integration_id : null;
        void verifyAndNotify(() => {
          showBrowserNotification({
            title: "FabChat — atendimento humano",
            body: "Uma conversa foi escalada para atendimento humano.",
            tag: `fabchat-escalation-${conversationId}`,
            url: buildConversationInboxUrl({
              conversationId,
              channel: parseChannel(escalationChannel),
              integrationId: escalationIntegrationId,
            }),
          });
        });
      }
    }
  }, [disabled, workspaceId, syncReadTimestampsFromStorage]);

  const handleConversationRealtime = useCallback<ConversationChangeHandler>(
    (payload) => {
      syncReadTimestampsFromStorage();
      const record = (payload.new || {}) as Record<string, unknown>;
      notifyFromConversationChange(record);

      const conversationId = typeof record.id === "string" ? record.id : null;
      const lastMessageAt =
        typeof record.last_message_at === "string" ? record.last_message_at : null;
      const unread = typeof record.unread_count === "number" ? record.unread_count : 0;

      // Leitura local à frente do banco: o badge precisa da contagem do servidor.
      if (
        conversationId &&
        unread > 0 &&
        !isUnreadForUser(conversationId, unread, lastMessageAt, readTimestamps.current)
      ) {
        void checkUnreadMessages();
        return;
      }

      const preview = typeof record.last_message_preview === "string" ? record.last_message_preview : null;
      const direction = typeof record.last_message_direction === "string" ? record.last_message_direction : null;
      if (conversationId && lastMessageAt && typeof record.unread_count === "number" && preview != null) {
        const stillUnread = isUnreadForUser(
          conversationId,
          unread,
          lastMessageAt,
          readTimestamps.current,
        );
        const patched = patchKnownUnreadPreview(unreadConversationsRef.current, {
          conversationId,
          preview,
          lastMessageAt,
          unreadCount: unread,
          isOutbound: direction === "outbound",
          stillUnread,
        });
        if (patched.applied) {
          unreadConversationsRef.current = patched.next;
          setUnreadConversations(patched.next);
          if (patched.membershipDelta !== 0) {
            setUnreadBadgeTotal((prev) =>
              prev == null ? prev : Math.max(0, prev + patched.membershipDelta),
            );
          }
          return;
        }
      }

      debouncedCheck();
    },
    [checkUnreadMessages, debouncedCheck, notifyFromConversationChange, syncReadTimestampsFromStorage],
  );

  // Reconcile ao retornar à aba: hub resume (pause-on-hidden=1) OU visibilitychange (flag=0).
  const handleHubReconcile = useCallback(() => {
    void checkUnreadMessages();
  }, [checkUnreadMessages]);

  useWorkspaceConversationRealtime(
    workspaceId,
    handleConversationRealtime,
    !disabled && Boolean(workspaceId),
    REALTIME_PAUSE_ON_HIDDEN_ENABLED ? handleHubReconcile : undefined,
  );

  useEffect(() => {
    if (disabled || !workspaceId) return;

    checkUnreadMessages();

    const interval = setInterval(() => {
      if (typeof document !== "undefined" && document.hidden) return;
      void checkUnreadMessages();
    }, UNREAD_RECONCILE_POLL_MS);

    // Com pause-on-hidden, o hub já reconcilia no resume — evita SELECT duplicado.
    let removeVisibilityReconcile: (() => void) | undefined;
    if (!REALTIME_PAUSE_ON_HIDDEN_ENABLED && typeof document !== "undefined") {
      const onVisibilityChange = () => {
        if (!document.hidden) void checkUnreadMessages();
      };
      document.addEventListener("visibilitychange", onVisibilityChange);
      removeVisibilityReconcile = () =>
        document.removeEventListener("visibilitychange", onVisibilityChange);
    }

    return () => {
      clearInterval(interval);
      removeVisibilityReconcile?.();
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, [workspaceId, checkUnreadMessages, disabled]);

  const unreadCount = unreadBadgeTotal ?? unreadConversations.length;
  const unreadPreviews = useMemo(
    () => unreadConversations.slice(0, PREVIEW_LIMIT),
    [unreadConversations],
  );
  const sidebarUnreads = useMemo(
    () => buildSidebarUnreadMaps(unreadConversations),
    [unreadConversations],
  );

  // Play sound when count increases
  useEffect(() => {
    if (disabled) return;
    if (unreadCount > prevCountRef.current && prevCountRef.current >= 0) {
      playNotificationSound();
    }
    prevCountRef.current = unreadCount;
  }, [unreadCount, disabled]);

  useEffect(() => {
    if (disabled) return;
    const onInboxPatch = () => {
      syncReadTimestampsFromStorage();
      // Reconcile via debounce (mesmo contrato ≤2s); evita SELECT imediato duplicado.
      debouncedCheck();
    };
    window.addEventListener(CONVERSATION_INBOX_PATCH_EVENT, onInboxPatch);
    return () => window.removeEventListener(CONVERSATION_INBOX_PATCH_EVENT, onInboxPatch);
  }, [disabled, debouncedCheck, syncReadTimestampsFromStorage]);

  // Listen for conversation-read event
  useEffect(() => {
    if (disabled) return;
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.conversationId) {
        const conversationId = detail.conversationId as string;
        readTimestamps.current.set(conversationId, new Date().toISOString());
        try {
          localStorage.setItem(storageKey, JSON.stringify(Object.fromEntries(readTimestamps.current)));
        } catch {
          // Ignore storage failures; unread state still updates in memory.
        }
        // Patch local imediato (header + sidebar); reconcile no debounce/realtime/poll.
        // Só decrementa se a conversa estava no conjunto unread — clique em já-lida
        // (lista / deep-link) não pode zerar o badge indevidamente.
        setUnreadConversations((prev) => {
          const wasUnread = prev.some((p) => p.conversationId === conversationId);
          if (wasUnread) {
            setUnreadBadgeTotal((total) => (total == null ? total : Math.max(0, total - 1)));
          }
          return wasUnread
            ? prev.filter((p) => p.conversationId !== conversationId)
            : prev;
        });
      }
      debouncedCheck();
    };
    window.addEventListener("conversation-read", handler);
    return () => window.removeEventListener("conversation-read", handler);
  }, [disabled, storageKey, debouncedCheck]);

  useEffect(() => {
    if (disabled) return;
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.conversationId) {
        const conversationId = detail.conversationId as string;
        readTimestamps.current.delete(conversationId);
        try {
          localStorage.setItem(
            storageKey,
            JSON.stringify(Object.fromEntries(readTimestamps.current)),
          );
        } catch {
          // Ignore storage failures.
        }
        setUnreadConversations((prev) => {
          const alreadyUnread = prev.some((p) => p.conversationId === conversationId);
          if (!alreadyUnread) {
            setUnreadBadgeTotal((total) => (total == null ? total : total + 1));
          }
          return prev;
        });
      }
      // Sem SELECT imediato — reconcile no debounce (≤2s) / realtime / poll.
      debouncedCheck();
    };
    window.addEventListener("conversation-unread", handler);
    return () => window.removeEventListener("conversation-unread", handler);
  }, [disabled, storageKey, debouncedCheck]);

  return {
    unreadCount: disabled ? 0 : unreadCount,
    unreadPreviews: disabled ? [] : unreadPreviews,
    sidebarUnreads: disabled ? EMPTY_SIDEBAR_UNREADS : sidebarUnreads,
  };
}
